import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Modal from '../../components/common/Modal';
import { updateLinkInRedis, deleteLinkFromRedis } from '../../lib/redisCache';
import { LayoutGrid, Folder, ChevronRight, LinkIcon } from 'lucide-react';
import SubscriptionCancelledScreen from '../../components/dashboard/SubscriptionCancelledScreen';
import { useToast } from '../../components/common/ToastProvider.jsx';
import QRCode from 'qrcode-svg';
import qrCodeIcon from '../../assets/qr-code-icon.svg';

const PLATFORMS = {
  meta: { name: 'Meta (FB/IG)', colorClass: 'text-blue-700 bg-blue-400/10' },
  google: { name: 'Google Ads', colorClass: 'text-emerald-700 bg-emerald-400/10' },
  tiktok: { name: 'TikTok Ads', colorClass: 'text-pink-700 bg-pink-400/10' },
  taboola: { name: 'Taboola', colorClass: 'text-orange-700 bg-orange-400/10' },
  outbrain: { name: 'Outbrain', colorClass: 'text-indigo-700 bg-indigo-400/10' },
};

const linkClickKey = (link) => `${link.domain ?? ''}/${link.slug ?? ''}`;

const KIND_BY_LEVEL = {
  0: 'workspace',
  1: 'campaign',
  2: 'group',
};

const KIND_LABEL = {
  workspace: 'Workspace',
  campaign: 'Campaign',
  group: 'Group',
};

const KIND_LABEL_PLURAL = {
  workspace: 'Workspaces',
  campaign: 'Campaigns',
  group: 'Groups',
};

const SPACE_NAME_REGEX = /^[A-Za-z0-9 !@#$%&-]+$/;

const LinkManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [userId, setUserId] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [links, setLinks] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [currentSpaceId, setCurrentSpaceId] = useState(null);
  const [presetsMap, setPresetsMap] = useState({}); // Map of preset ID to preset data
  const [clickCountsMap, setClickCountsMap] = useState({}); // key: domain/slug -> count (clicks without link_id)
  const [clickCountsByLinkId, setClickCountsByLinkId] = useState({}); // link_id -> count

  const getLinkClickDisplayCount = useCallback(
    (link) =>
      (clickCountsByLinkId[link.id] ?? 0) + (clickCountsMap[linkClickKey(link)] ?? 0),
    [clickCountsByLinkId, clickCountsMap]
  );
  const [loading, setLoading] = useState(true);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [openSpaceMenuId, setOpenSpaceMenuId] = useState(null);
  const [spaceModal, setSpaceModal] = useState({
    isOpen: false,
    mode: 'create', // 'create' | 'edit'
    spaceId: null,
    kind: null,
    name: '',
    error: null,
    isLoading: false,
  });
  const spaceNameInputRef = useRef(null);

  useEffect(() => {
    if (!spaceModal.isOpen) return;
    const t = setTimeout(() => {
      spaceNameInputRef.current?.focus?.();
      spaceNameInputRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [spaceModal.isOpen]);
  const [utmPresetsModal, setUtmPresetsModal] = useState({
    isOpen: false,
    link: null,
  });
  const [moveModal, setMoveModal] = useState({
    isOpen: false,
    link: null,
    selectedSpaceId: null,
    isSaving: false,
    error: null,
  });
  const [qrModal, setQrModal] = useState({ isOpen: false, link: null });
  const [qrCopyStatus, setQrCopyStatus] = useState('');

  const getQrSvgString = useCallback((url) => {
    const qr = new QRCode(url, { container: 'svg-viewbox', join: true, background: '#ffffff' });
    return qr.svg();
  }, []);

  const handleDownloadQrPng = useCallback(async (shortUrl) => {
    try {
      const apiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&bgcolor=FFFFFF&data=${encodeURIComponent(shortUrl)}`;
      const res = await fetch(apiUrl);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = 'qrcode.png';
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      console.error('QR PNG download failed', e);
    }
  }, []);

  const handleDownloadQrSvg = useCallback(
    (shortUrl) => {
      try {
        const svgString = getQrSvgString(shortUrl);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = 'qrcode.svg';
        a.click();
        URL.revokeObjectURL(u);
      } catch (e) {
        console.error('QR SVG download failed', e);
      }
    },
    [getQrSvgString]
  );

  const handleCopyQrSvg = useCallback(
    async (shortUrl) => {
      try {
        const svgString = getQrSvgString(shortUrl);
        await navigator.clipboard.writeText(svgString);
        setQrCopyStatus('Copied!');
        setTimeout(() => setQrCopyStatus(''), 2000);
      } catch (e) {
        console.error('Copy SVG failed', e);
        setQrCopyStatus('Failed');
        setTimeout(() => setQrCopyStatus(''), 2000);
      }
    },
    [getQrSvgString]
  );

  // Modal states
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
    onConfirm: null,
    isLoading: false,
  });
  const [detailsLink, setDetailsLink] = useState(null);
  const [detailsPixelNames, setDetailsPixelNames] = useState([]);

  useEffect(() => {
    if (!detailsLink) { setDetailsPixelNames([]); return; }
    const ids = (detailsLink.pixels || [])
      .map((p) => (typeof p === 'string' ? p : p?.id))
      .filter(Boolean);
    if (ids.length === 0) { setDetailsPixelNames([]); return; }
    supabase
      .from('pixels')
      .select('id, name')
      .in('id', ids)
      .then(({ data }) => setDetailsPixelNames(data || []));
  }, [detailsLink]);

  const getDetailsUrlStatus = (url) => {
    if (!detailsLink || !url || !Array.isArray(detailsLink.urls_status)) return 'active';
    const match = detailsLink.urls_status.find((item) => item?.url === url);
    const raw = (match?.status || '').toLowerCase();
    if (raw === 'active' || raw === 'approve') return 'active';
    if (raw === 'rejected' || raw === 'reject') return 'rejected';
    return 'active';
  };

  const urlStatusBadge = (status) => {
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#047857]/15 text-[#047857]">
          Approved
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-red-600">
        Rejected
      </span>
    );
  };

  const [deleteLinkModal, setDeleteLinkModal] = useState({
    isOpen: false,
    link: null,
    isLoading: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Sync current space from URL query (supports refresh/back/forward)
  useEffect(() => {
    const querySpaceId = searchParams.get('space_id') || null;
    setCurrentSpaceId((prev) => (prev === querySpaceId ? prev : querySpaceId));
  }, [searchParams]);

  useEffect(() => {
    if (!createMenuOpen && !openSpaceMenuId) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (createMenuOpen) setCreateMenuOpen(false);
        if (openSpaceMenuId) setOpenSpaceMenuId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [createMenuOpen, openSpaceMenuId]);

  const fetchData = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);

      // Fetch plan type to control folders feature availability
      let currentPlanType = null;
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan_type, subscription_status')
        .eq('user_id', user.id)
        .single();
      if (profile?.plan_type) {
        currentPlanType = profile.plan_type;
        setPlanType(profile.plan_type);
      } else {
        setPlanType(null);
      }
      setSubscriptionStatus(profile?.subscription_status ?? null);

      // Fetch links
      const { data: linksData, error: linksError } = await supabase
        .from('links')
        .select('*')
        .eq('user_id', user.id)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false });

      if (linksError) throw linksError;

      // Collect all unique preset IDs from all links
      const presetIds = new Set();
      (linksData || []).forEach((link) => {
        if (Array.isArray(link.utm_presets)) {
          link.utm_presets.forEach((id) => presetIds.add(id));
        }
      });

      // Fetch all presets if there are any
      let presetsDataMap = {};
      if (presetIds.size > 0) {
        const { data: presetsData, error: presetsError } = await supabase
          .from('utm_presets')
          .select('*')
          .in('id', Array.from(presetIds))
          .eq('user_id', user.id);

        if (!presetsError && presetsData) {
          presetsDataMap = presetsData.reduce((acc, preset) => {
            acc[preset.id] = preset;
            return acc;
          }, {});
        }
      }

      setPresetsMap(presetsDataMap);

      // Fetch click counts: by link_id when present (authoritative), else domain/slug for legacy rows
      const { data: clicksData, error: clicksError } = await supabase
        .from('clicks')
        .select('domain, slug, link_id')
        .eq('user_id', user.id);

      const counts = {};
      const byLinkId = {};
      if (!clicksError && clicksData) {
        clicksData.forEach((c) => {
          if (c.link_id) {
            byLinkId[c.link_id] = (byLinkId[c.link_id] || 0) + 1;
          } else {
            const key = `${c.domain ?? ''}/${c.slug ?? ''}`;
            counts[key] = (counts[key] || 0) + 1;
          }
        });
      }

      // Fetch hierarchy spaces (workspace/campaign/group) only for plans that support folders.
      // If table doesn't exist yet, we keep dashboard fully functional with links only.
      let spacesData = [];
      const normalizedPlan = (currentPlanType || '').toLowerCase();
      const foldersDisabled = normalizedPlan === 'free' || normalizedPlan === 'start';
      if (!foldersDisabled) {
        const { data: fetchedSpaces, error: spacesError } = await supabase
          .from('link_spaces')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (!spacesError && fetchedSpaces) {
          spacesData = fetchedSpaces;
        } else if (spacesError) {
          console.warn('[LinkManager] link_spaces table not ready yet:', spacesError.message);
        }
      }

      setSpaces(spacesData);
      setClickCountsMap(counts);
      setClickCountsByLinkId(byLinkId);
      setLinks(linksData || []);
    } catch (error) {
      console.error('Error fetching links:', error);
    } finally {
      setLoading(false);
    }
  };

  const spaceById = useMemo(
    () =>
      spaces.reduce((acc, s) => {
        acc[s.id] = s;
        return acc;
      }, {}),
    [spaces]
  );

  const currentSpace = currentSpaceId ? spaceById[currentSpaceId] || null : null;
  const currentLevel = currentSpace?.level ?? 0;
  const nextKind = KIND_BY_LEVEL[currentLevel] || null;
  const normalizedPlanType = (planType || '').toLowerCase();
  const isFoldersFeatureDisabled = normalizedPlanType === 'free' || normalizedPlanType === 'start';
  const isFoldersEnabled = !isFoldersFeatureDisabled;

  const goToSpace = (spaceId) => {
    const normalized = spaceId || null;
    setCurrentSpaceId(normalized);
    const next = new URLSearchParams(searchParams);
    if (normalized) {
      next.set('space_id', normalized);
    } else {
      next.delete('space_id');
    }
    setSearchParams(next, { replace: true });
  };

  // If URL contains stale/deleted space_id, reset to root
  useEffect(() => {
    if (!loading && currentSpaceId && !spaceById[currentSpaceId]) {
      goToSpace(null);
    }
  }, [loading, currentSpaceId, spaceById]);

  // Free/Starter always use a flat links page (no folders)
  useEffect(() => {
    if (isFoldersFeatureDisabled && currentSpaceId) {
      goToSpace(null);
    }
  }, [isFoldersFeatureDisabled, currentSpaceId]);

  const childSpaces = useMemo(() => {
    if (!nextKind) return [];
    return spaces
      .filter((s) => (s.parent_id || null) === (currentSpaceId || null) && s.kind === nextKind)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [spaces, currentSpaceId, nextKind]);

  const directLinks = useMemo(
    () =>
      isFoldersEnabled
        ? links.filter((l) => (l.space_id || null) === (currentSpaceId || null))
        : links,
    [links, currentSpaceId, isFoldersEnabled]
  );

  const getDescendantIds = (spaceId) => {
    const out = [];
    const stack = [spaceId];
    while (stack.length) {
      const parent = stack.pop();
      const kids = spaces.filter((s) => (s.parent_id || null) === parent);
      kids.forEach((k) => {
        out.push(k.id);
        stack.push(k.id);
      });
    }
    return out;
  };

  const getSpaceStats = (spaceId) => {
    const ids = [spaceId, ...getDescendantIds(spaceId)];
    const idsSet = new Set(ids);
    const scoped = links.filter((l) => idsSet.has(l.space_id || null));
    const clicks = scoped.reduce((sum, link) => sum + getLinkClickDisplayCount(link), 0);
    return { linksCount: scoped.length, clicks };
  };

  const breadcrumbs = useMemo(() => {
    if (!currentSpaceId || !currentSpace) return [];
    const out = [];
    let cursor = currentSpace;
    while (cursor) {
      out.push(cursor);
      cursor = cursor.parent_id ? spaceById[cursor.parent_id] : null;
    }
    return out.reverse();
  }, [currentSpaceId, currentSpace, spaceById]);

  const moveSelectionPath = useMemo(() => {
    if (!moveModal.selectedSpaceId) return [];
    const out = [];
    let cursor = spaceById[moveModal.selectedSpaceId] || null;
    while (cursor) {
      out.push(cursor);
      cursor = cursor.parent_id ? spaceById[cursor.parent_id] : null;
    }
    return out.reverse();
  }, [moveModal.selectedSpaceId, spaceById]);

  const moveChildSpaces = useMemo(
    () =>
      spaces
        .filter((s) => (s.parent_id || null) === (moveModal.selectedSpaceId || null))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [spaces, moveModal.selectedSpaceId]
  );
  const moveChildKind = useMemo(() => {
    const selectedLevel = moveModal.selectedSpaceId
      ? (spaceById[moveModal.selectedSpaceId]?.level ?? 0)
      : 0;
    return KIND_BY_LEVEL[selectedLevel] || null;
  }, [moveModal.selectedSpaceId, spaceById]);
  const moveChildLabelPlural = moveChildKind
    ? KIND_LABEL_PLURAL[moveChildKind] || 'Spaces'
    : 'Spaces';

  const openCreateSpaceModal = (kind) => {
    setCreateMenuOpen(false);
    setSpaceModal({
      isOpen: true,
      mode: 'create',
      spaceId: null,
      kind,
      name: '',
      error: null,
      isLoading: false,
    });
  };

  const openEditSpaceModal = (space) => {
    setOpenSpaceMenuId(null);
    setSpaceModal({
      isOpen: true,
      mode: 'edit',
      spaceId: space.id,
      kind: space.kind,
      name: space.name || '',
      error: null,
      isLoading: false,
    });
  };

  const closeCreateSpaceModal = () => {
    if (spaceModal.isLoading) return;
    setSpaceModal((prev) => ({ ...prev, isOpen: false }));
  };

  const validateSpaceName = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return 'Name is required.';
    if (trimmed.length < 2) return 'Name must be at least 2 characters.';
    if (trimmed.length > 80) return 'Name cannot exceed 80 characters.';
    if (!SPACE_NAME_REGEX.test(trimmed)) {
      return 'Allowed: English letters, numbers, spaces, and !@#$%&-';
    }
    return null;
  };

  const handleSaveSpace = async () => {
    const validationError = validateSpaceName(spaceModal.name);
    if (validationError) {
      setSpaceModal((prev) => ({ ...prev, error: validationError }));
      return;
    }
    if (!userId) {
      setSpaceModal((prev) => ({ ...prev, error: 'You must be logged in.' }));
      return;
    }

    try {
      setSpaceModal((prev) => ({ ...prev, isLoading: true, error: null }));
      if (spaceModal.mode === 'edit' && spaceModal.spaceId) {
        const { error } = await supabase
          .from('link_spaces')
          .update({
            name: spaceModal.name.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', spaceModal.spaceId)
          .eq('user_id', userId);
        if (error) throw error;
      } else {
        const nextLevel = currentSpaceId ? (currentSpace?.level || 0) + 1 : 1;
        if (nextLevel > 3) {
          setSpaceModal((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Maximum depth reached (3 levels).',
          }));
          return;
        }
        const payload = {
          user_id: userId,
          name: spaceModal.name.trim(),
          kind: spaceModal.kind,
          level: nextLevel,
          parent_id: currentSpaceId || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        const { error } = await supabase.from('link_spaces').insert(payload);
        if (error) throw error;
      }
      setSpaceModal((prev) => ({ ...prev, isOpen: false, isLoading: false }));
      await fetchData();
    } catch (error) {
      const dbMessage = String(error?.message || '');
      const isDuplicateSpaceName =
        error?.code === '23505' ||
        /duplicate key value/i.test(dbMessage) ||
        /link_spaces_user_id_parent_id_name_key/i.test(dbMessage);
      const spaceKindLabel = (KIND_LABEL[spaceModal.kind] || 'item').toLowerCase();
      const friendlyError = isDuplicateSpaceName
        ? `You already have a ${spaceKindLabel} with this name.`
        : error?.message || 'Failed to create item. Please try again.';

      setSpaceModal((prev) => ({
        ...prev,
        isLoading: false,
        error: friendlyError,
      }));
    }
  };

  const handleCancelSpace = async (space) => {
    setOpenSpaceMenuId(null);
    const stats = getSpaceStats(space.id);
    if (stats.linksCount > 0) {
      setModalState({
        isOpen: true,
        type: 'alert',
        title: 'Cannot cancel this item',
        message:
          'This item contains links. You cannot cancel it until you move or delete all links inside it.',
        onConfirm: null,
        isLoading: false,
      });
      return;
    }
    setModalState({
      isOpen: true,
      type: 'delete',
      title: `Delete ${KIND_LABEL[space.kind] || 'item'}?`,
      message: (
        <>
          Are you sure you want to delete <strong>{space.name}</strong>?
        </>
      ),
      onConfirm: async () => {
        setModalState((prev) => ({ ...prev, isLoading: true }));
        try {
          const { error } = await supabase
            .from('link_spaces')
            .delete()
            .eq('id', space.id)
            .eq('user_id', userId);
          if (error) throw error;

          if (currentSpaceId === space.id) {
            goToSpace(space.parent_id || null);
          }
          setModalState((prev) => ({ ...prev, isOpen: false, isLoading: false }));
          await fetchData();
        } catch (error) {
          setModalState({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error?.message || 'Failed to delete item. Please try again.',
            onConfirm: null,
            isLoading: false,
          });
        }
      },
      isLoading: false,
    });
  };

  const handleCreateOption = (option) => {
    setCreateMenuOpen(false);
    if (option === 'link') {
      const query =
        isFoldersEnabled && currentSpaceId ? `?space_id=${encodeURIComponent(currentSpaceId)}` : '';
      navigate(`/dashboard/links/new${query}`);
      return;
    }
    if (!isFoldersEnabled) return;
    openCreateSpaceModal(option);
  };

  const createOptions = useMemo(() => {
    const options = [{ id: 'link', label: 'New Link' }];
    if (isFoldersEnabled && nextKind)
      options.push({ id: nextKind, label: `New ${KIND_LABEL[nextKind]}` });
    return options;
  }, [nextKind, isFoldersEnabled]);

  const openMoveModal = (linkToMove) => {
    if (!isFoldersEnabled || !linkToMove) return;
    setMoveModal({
      isOpen: true,
      link: linkToMove,
      selectedSpaceId: linkToMove.space_id || null,
      isSaving: false,
      error: null,
    });
  };

  const closeMoveModal = () => {
    if (moveModal.isSaving) return;
    setMoveModal({
      isOpen: false,
      link: null,
      selectedSpaceId: null,
      isSaving: false,
      error: null,
    });
  };

  const handleDeleteLink = async () => {
    const link = deleteLinkModal.link;
    if (!link) return;
    setDeleteLinkModal((prev) => ({ ...prev, isLoading: true }));
    try {
      const deletedAt = new Date().toISOString();
      const { error } = await supabase
        .from('links')
        .update({ status: 'deleted', deleted_at: deletedAt })
        .eq('id', link.id);
      if (error) throw error;
      try {
        await deleteLinkFromRedis(link.domain, link.slug);
      } catch (redisError) {
        console.warn('⚠️ [LinkManager] Failed to delete from Redis:', redisError);
      }
      setDeleteLinkModal({ isOpen: false, link: null, isLoading: false });
      fetchData();
      showToast({
        type: 'success',
        title: 'Link deleted',
        message: 'The link was removed successfully.',
      });
    } catch (err) {
      console.error('Error deleting link:', err);
      setDeleteLinkModal((prev) => ({ ...prev, isLoading: false }));
      setModalState({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Error deleting link. Please try again.',
        onConfirm: null,
        isLoading: false,
      });
    }
  };

  const handleMoveLink = async () => {
    if (!moveModal.link || !userId) return;
    const targetSpaceId = moveModal.selectedSpaceId || null;
    const currentLinkSpaceId = moveModal.link.space_id || null;
    if (targetSpaceId === currentLinkSpaceId) {
      closeMoveModal();
      return;
    }

    try {
      setMoveModal((prev) => ({ ...prev, isSaving: true, error: null }));
      const { error } = await supabase
        .from('links')
        .update({
          space_id: targetSpaceId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', moveModal.link.id)
        .eq('user_id', userId);
      if (error) throw error;

      // Keep QStash/Upstash cache in sync with new location
      try {
        await updateLinkInRedis(
          { ...moveModal.link, space_id: targetSpaceId, updated_at: new Date().toISOString() },
          supabase
        );
      } catch (cacheError) {
        console.warn('⚠️ [LinkManager] Failed to sync moved link in cache:', cacheError);
      }

      closeMoveModal();
      await fetchData();
    } catch (error) {
      setMoveModal((prev) => ({
        ...prev,
        isSaving: false,
        error: error?.message || 'Failed to move link. Please try again.',
      }));
    }
  };

  const handleCopy = async (url, copyBtnId = null) => {
    try {
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here
      if (copyBtnId) {
        // Visual feedback for preset copy
        const element = document.getElementById(copyBtnId);
        if (element) {
          const originalText = element.innerHTML;
          element.innerHTML = '<span class="material-symbols-outlined text-base" aria-hidden="true">check</span>';
          setTimeout(() => {
            element.innerHTML = originalText;
          }, 1000);
        }
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const buildPresetUrl = (link, preset) => {
    const baseUrl = link.short_url || '';
    if (!preset || !baseUrl) return baseUrl;

    const params = [];
    if (preset.utm_source) params.push(`utm_source=${preset.utm_source}`);
    if (preset.utm_medium) params.push(`utm_medium=${preset.utm_medium}`);
    if (preset.utm_campaign) params.push(`utm_campaign=${preset.utm_campaign}`);
    if (preset.utm_content) params.push(`utm_content=${preset.utm_content}`);
    if (preset.utm_term) params.push(`utm_term=${preset.utm_term}`);

    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
  };

  const renderUtmPresetsList = (link) => {
    if (!link?.utm_presets || !Array.isArray(link.utm_presets) || link.utm_presets.length === 0) {
      return null;
    }

    return (
      <div className="space-y-2">
        {link.utm_presets.map((presetId) => {
          const preset = presetsMap[presetId];
          if (!preset) return null;

          const platformInfo = PLATFORMS[preset.platform] || {
            name: preset.platform,
            colorClass: 'text-[#1b1b1b] bg-[#1b1b1b]/10',
          };

          const presetUrl = buildPresetUrl(link, preset);
          const copyBtnId = `copy-btn-${link.id}-${presetId}`;

          return (
            <div
              key={presetId}
              className="p-3 bg-white rounded-lg border border-slate-200 space-y-2"
            >
              {/* Preset Header */}
              <div className="flex items-center gap-2">
                <div className={`px-2 py-1 rounded text-xs font-bold ${platformInfo.colorClass}`}>
                  {platformInfo.name}
                </div>
                <span className="text-xs text-slate-500">•</span>
                <span
                  className="text-xs text-slate-700 font-medium truncate flex-1"
                  title={preset.name}
                >
                  {preset.name}
                </span>
                <span className="text-xs text-slate-500">({link.domain})</span>
              </div>

              {/* Preset URL */}
              <div className="flex items-start gap-2 min-w-0">
                <span
                  className="font-mono text-base font-bold text-emerald-600 flex-1 min-w-0 break-all whitespace-normal"
                  title={presetUrl}
                  style={{ fontWeight: '700' }}
                >
                  {presetUrl}
                </span>
                <button
                  id={copyBtnId}
                  onClick={() => handleCopy(presetUrl, copyBtnId)}
                  className="text-[#1b1b1b] hover:text-primary transition-colors p-1.5 rounded flex-shrink-0 mt-0.5"
                  title="Copy Preset URL"
                  aria-label="Copy preset URL"
                >
                  <span className="material-symbols-outlined text-base" aria-hidden="true">content_copy</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return '—';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleToggleStatus = async (linkId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'PAUSED' : 'active';

    // Optimistic update: change UI immediately so the toggle moves right away
    setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...l, status: newStatus } : l)));

    try {
      const { error } = await supabase.from('links').update({ status: newStatus }).eq('id', linkId);

      if (error) {
        throw error;
      }

      // Best-effort: keep Redis in sync as well
      try {
        const linkToUpdate = links.find((l) => l.id === linkId);
        if (linkToUpdate) {
          await updateLinkInRedis({ ...linkToUpdate, status: newStatus }, supabase);
        }
      } catch (redisError) {
        console.warn('⚠️ [LinkManager] Failed to sync Redis after status toggle:', redisError);
      }
    } catch (error) {
      console.error('Error updating link status:', error);
      // Revert optimistic update on error
      setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...l, status: currentStatus } : l)));
      setModalState({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to change link state (Active/Paused). Please try again.',
        onConfirm: null,
        isLoading: false,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-8 w-full h-full items-center justify-center" role="status">
        <div className="text-center py-12">
          <span className="material-symbols-outlined text-4xl text-slate-600 animate-spin" aria-hidden="true">
            refresh
          </span>
          <p className="text-[#1b1b1b] mt-4">Loading links...</p>
        </div>
      </div>
    );
  }

  if (subscriptionStatus === 'cancelled') {
    return <SubscriptionCancelledScreen />;
  }

  const pageTitle = isFoldersEnabled
    ? currentLevel === 0
      ? 'Workspaces'
      : currentLevel === 1
        ? 'Campaigns'
        : 'Groups'
    : 'Link Manager';
  const showBackArrow = isFoldersEnabled && !!currentSpaceId;
  const showHomeButton = isFoldersEnabled && !currentSpaceId;

  return (
    <div className="flex flex-col gap-6 md:gap-8 w-full max-w-7xl mx-auto">
      <div
        className={
          isFoldersEnabled
            ? 'sticky top-0 z-30 relative lg:-mt-6 lg:pt-6 -mx-4 px-4 py-3 md:-mx-6 md:px-6 bg-white border-b border-slate-200 flex flex-col gap-4'
            : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
        }
      >
        {isFoldersEnabled && (
          <div className="pointer-events-none absolute inset-x-0 -top-20 h-20 lg:-top-6 lg:h-6 bg-white" aria-hidden="true"></div>
        )}
        <div
          className={
            isFoldersEnabled
              ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
              : 'flex items-center justify-between gap-4 w-full'
          }
        >
          <div
            className={`flex flex-col gap-2 flex-1 min-w-0 ${isFoldersEnabled ? 'hidden md:flex' : 'flex'}`}
          >
            <div className="flex items-center gap-3">
              {isFoldersEnabled && (
                <button
                  type="button"
                  onClick={
                    showBackArrow
                      ? () => goToSpace(currentSpace?.parent_id || null)
                      : showHomeButton
                        ? () => goToSpace(null)
                        : undefined
                  }
                  className={`flex items-center justify-center w-11 h-11 rounded-2xl border border-slate-200 text-gray-400 hover:bg-slate-200 hover:text-[#1b1b1b] transition-all active:scale-90 flex-shrink-0 ${
                    showBackArrow || showHomeButton ? '' : 'opacity-0 pointer-events-none'
                  }`}
                  aria-label={showBackArrow ? 'Back' : 'Home'}
                >
                  <span className="material-symbols-outlined text-2xl" aria-hidden="true">
                    {showBackArrow ? 'chevron_left' : 'home'}
                  </span>
                </button>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-[#1b1b1b] truncate">
                {pageTitle}
              </h1>
            </div>
          </div>
          <div className={isFoldersEnabled ? 'relative w-full sm:w-auto' : 'relative ml-auto'}>
            <button
              onClick={() =>
                createOptions.length === 1
                  ? handleCreateOption('link')
                  : setCreateMenuOpen((v) => !v)
              }
              className={`flex items-center justify-center gap-2 ${isFoldersEnabled ? 'w-full sm:w-auto' : 'w-auto'} px-6 py-3 md:py-2.5 text-white font-bold rounded-xl transition-colors shadow-lg text-base md:text-sm bg-[#a855f7] hover:bg-[#9333ea]`}
              {...(createOptions.length > 1
                ? { 'aria-expanded': createMenuOpen, 'aria-haspopup': 'true' }
                : {})}
            >
              <span className="material-symbols-outlined text-xl md:text-base" aria-hidden="true">add</span>
              {createOptions.length === 1 ? 'New Link' : 'Create'}
            </button>
            {createMenuOpen && createOptions.length > 1 && (
              <>
                <button
                  className="fixed inset-0 z-10"
                  aria-label="Close create menu"
                  onClick={() => setCreateMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 z-20 rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                  {createOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleCreateOption(option.id)}
                      className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        {isFoldersEnabled && (
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700">
            <button
              type="button"
              onClick={() => goToSpace(null)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors"
            >
              <span className="material-symbols-outlined text-sm" aria-hidden="true">home</span>
              Root
            </button>
            {breadcrumbs.map((b) => (
              <React.Fragment key={b.id}>
                <ChevronRight size={12} className="text-slate-500" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => goToSpace(b.id)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors"
                >
                  {b.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        )}
      </div>

      {/* Active Grid cards for current level children */}
      {isFoldersEnabled && childSpaces.length > 0 && (
        <div className="flex flex-col gap-6 w-full">
          <div className="relative flex py-5 items-center">
            <div className="flex-grow border-t border-[#E5E9E8]"></div>
            <span
              className="flex-shrink mx-4 text-xs font-bold uppercase px-3 py-1 rounded-full bg-[#F4F7F6]"
              style={{ color: '#001E22' }}
            >
              {KIND_LABEL_PLURAL[nextKind] || 'SPACES'}
            </span>
            <div className="flex-grow border-t border-[#E5E9E8]"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full overflow-visible pt-1">
            {childSpaces.map((space) => {
              const stats = getSpaceStats(space.id);
              const kindLabel = KIND_LABEL[space.kind] || 'Campaign';
              return (
                <div
                  key={space.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => goToSpace(space.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goToSpace(space.id);
                    }
                  }}
                  className={`group relative w-full min-h-[224px] transition-all duration-300 overflow-visible ${openSpaceMenuId === space.id ? 'z-20' : ''}`}
                  dir="ltr"
                >
                  {/* Folder shape: LTR – path and line settings from Clean Folder Structure UI LTR example */}
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 300 224"
                    preserveAspectRatio="none"
                    fill="none"
                    aria-hidden="true"
                    style={{ shapeRendering: 'geometricPrecision' }}
                  >
                    <path
                      d="M 1,1 H 60 L 75,16 H 299 V 223 H 1 Z"
                      fill="#FCFDFD"
                      stroke="#a855f7"
                      strokeWidth="1.5"
                      style={{
                        vectorEffect: 'non-scaling-stroke',
                        shapeRendering: 'geometricPrecision',
                      }}
                      className="transition-all duration-300 group-hover:stroke-[#9333ea] group-hover:fill-[#F4F7F6]"
                    />
                  </svg>
                  <div className="relative z-10 flex flex-col min-h-[224px] p-6 pt-10">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold mb-1 group-hover:text-primary transition-colors">
                          {space.name}
                        </h3>
                        <div className="flex items-center gap-2 text-[#a855f7] text-xs font-bold uppercase tracking-widest opacity-80">
                          <LayoutGrid size={14} aria-hidden="true" />
                          <span>{kindLabel}</span>
                        </div>
                      </div>
                      <div className="mr-12 text-[#a855f7] transition-all" aria-hidden="true">
                        <Folder size={24} />
                      </div>
                    </div>
                    <div className="absolute top-8 right-4">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenSpaceMenuId((prev) => (prev === space.id ? null : space.id));
                        }}
                        className="p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-[#1b1b1b] hover:border-primary/40 transition-colors"
                        aria-label="Space actions"
                        aria-expanded={openSpaceMenuId === space.id}
                        aria-haspopup="true"
                      >
                        <span className="material-symbols-outlined text-base" aria-hidden="true">more_vert</span>
                      </button>
                      {openSpaceMenuId === space.id && (
                        <div
                          className="absolute right-0 mt-2 w-40 rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden z-20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => openEditSpaceModal(space)}
                            className="w-full px-4 py-2.5 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors text-sm"
                          >
                            Update
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCancelSpace(space)}
                            className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-red-400/10 transition-colors text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-card-bg border border-card-border rounded-xl p-4 mb-6">
                      <div className="text-[10px] text-gray-500 font-bold uppercase mb-1 tracking-widest">
                        Total {kindLabel} Clicks
                      </div>
                      <div className="text-2xl font-extrabold text-[#1b1b1b]">
                        {new Intl.NumberFormat('en-US').format(stats.clicks)}
                      </div>
                    </div>

                    <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-200">
                      <div className="text-xs font-bold text-[#1b1b1b]">
                        {new Intl.NumberFormat('en-US').format(stats.linksCount)} Links Inside
                      </div>
                      <div className="flex items-center gap-2 text-xs font-bold text-[#1b1b1b]">
                        <div className="w-2 h-2 rounded-full bg-[#00F2B5] animate-pulse shadow-[0_0_8px_#00F2B5]" aria-hidden="true"></div>
                        Active
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isFoldersEnabled && openSpaceMenuId && (
        <button
          type="button"
          aria-label="Close space menu"
          className="fixed inset-0 z-[15]"
          onClick={() => setOpenSpaceMenuId(null)}
        />
      )}
      {/* Individual links divider */}
      {directLinks.length > 0 && (
        <div className="relative flex py-5 items-center">
          <div className="flex-grow border-t border-[#E5E9E8]"></div>
          <span
            className="flex-shrink mx-4 text-xs font-bold uppercase px-3 py-1 rounded-full bg-[#F4F7F6]"
            style={{ color: '#001E22' }}
          >
            Links
          </span>
          <div className="flex-grow border-t border-[#E5E9E8]"></div>
        </div>
      )}
      {/* Links List */}
      {directLinks.length === 0 &&
      childSpaces.length === 0 &&
      (!isFoldersEnabled || currentLevel === 0) ? (
        <div className="bg-[#fcfdfd] border rounded-2xl p-12 text-center hover:shadow-card-mint transition-all border-[#a855f7]/40 md:border-card-border md:hover:border-[#a855f7]/40">
          <span className="material-symbols-outlined text-6xl text-black mb-4" aria-hidden="true">link</span>
          <h3 className="text-xl font-bold text-black mb-2">No Links or Workspaces Yet</h3>
          <p className="text-black mb-6">
            Create your first link or workspace to start organizing your tracking.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard/links/new')}
              className="px-6 py-3 bg-[#a855f7] hover:bg-[#9333ea] text-white font-bold rounded-xl transition-all"
            >
              Create First Link
            </button>
            {isFoldersEnabled && (
              <button
                type="button"
                onClick={() => openCreateSpaceModal('workspace')}
                className="px-6 py-3 bg-white border border-[#a855f7] text-[#a855f7] font-bold rounded-xl transition-all hover:bg-[#f3f2ff]"
              >
                Create First Workspace
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {directLinks.map((link) => {
            const isActive = link.status === 'active';
            const isRejected = link.status === 'rejected';
            return (
              <div
                key={link.id}
                className={`relative bg-card-bg border rounded-[1.25rem] p-6 flex flex-col h-full transition-all duration-300 ease-out overflow-visible hover:shadow-card-mint ${
                  !isActive && !isRejected ? 'opacity-70' : ''
                } ${
                  isActive
                    ? 'border-[#0b996f] md:border-card-border md:hover:border-[#0b996f] md:hover:shadow-[0_12px_30px_rgba(19,91,236,0.15)]'
                    : 'border-card-border'
                }`}
              >
                {/* Header: title, destination, menu */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1 min-w-0 pr-4">
                    <h3
                      className="text-4xl font-bold text-[#1b1b1b] mb-1 break-words"
                      title={link.name || 'Untitled'}
                    >
                      {link.name || 'Untitled'}
                    </h3>
                    <p className="text-xs text-gray-500 truncate" title={link.target_url}>
                      {truncateText(link.target_url, 60)}
                    </p>
                  </div>
                  <div className="mr-12 text-[#0b996f] -rotate-3 transition-all" aria-hidden="true">
                    <LinkIcon size={24} />
                  </div>
                </div>
                <LinkActionsMenu
                  className="absolute top-4 right-4"
                  hoverBorderClass="hover:border-[#0b996f]/60"
                  link={link}
                  onDetails={(linkToShow) => setDetailsLink(linkToShow)}
                  onEdit={(linkToEdit) => navigate(`/dashboard/links/edit/${linkToEdit.id}`)}
                  onDuplicate={(linkToDuplicate) =>
                    navigate(
                      `/dashboard/links/new?duplicate=${linkToDuplicate.id}${currentSpaceId ? `&space_id=${encodeURIComponent(currentSpaceId)}` : ''}`
                    )
                  }
                  onAnalytics={(linkForAnalytics) =>
                    navigate(
                      `/dashboard/analytics?link_id=${encodeURIComponent(linkForAnalytics.id)}&domain=${encodeURIComponent(linkForAnalytics.domain || '')}&slug=${encodeURIComponent(linkForAnalytics.slug || '')}`
                    )
                  }
                  onMove={isFoldersEnabled ? openMoveModal : null}
                  onDeleteClick={(linkToDelete) =>
                    setDeleteLinkModal({ isOpen: true, link: linkToDelete, isLoading: false })
                  }
                />

                {/* Short Link box */}
                <div className="bg-card-bg border border-card-border rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[10px] text-gray-500 font-bold uppercase mb-1 tracking-widest">
                        Short Link
                      </span>
                      <span
                        className="font-bold text-lg tracking-wide break-all whitespace-normal text-[#0b996f]"
                        title={link.short_url}
                      >
                        {link.short_url}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopy(link.short_url)}
                      className="copy-btn p-2 bg-white hover:bg-slate-200 text-slate-700 rounded-lg transition-all flex-shrink-0 active:scale-90 border border-slate-200"
                      title="Copy to clipboard"
                      aria-label="Copy short link to clipboard"
                    >
                      <span className="material-symbols-outlined text-base" aria-hidden="true">content_copy</span>
                    </button>
                  </div>
                </div>

                {/* Footer: Active (left), QR (center), Clicks (right) */}
                <div className="mt-auto flex justify-between items-center pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-3">
                    {isRejected ? (
                      <span className="text-sm font-semibold text-red-600">Rejected</span>
                    ) : (
                      <>
                        <label className="relative inline-block w-11 h-6 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => handleToggleStatus(link.id, link.status || 'active')}
                            className="absolute opacity-0 w-0 h-0 peer"
                            aria-label="Toggle link status"
                          />
                          <span className="absolute inset-0 rounded-full bg-[#374151] transition-colors peer-checked:bg-[#00F59B]" />
                          <span className="absolute left-[3px] bottom-[3px] w-[18px] h-[18px] bg-white rounded-full transition-transform pointer-events-none peer-checked:translate-x-5" />
                        </label>
                        <span
                          className={`text-sm font-semibold ${isActive ? 'text-slate-700' : 'text-gray-500'}`}
                        >
                          {isActive ? 'Active' : 'Paused'}
                        </span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setQrModal({ isOpen: true, link })}
                    className="relative p-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-all active:scale-90 flex items-center justify-center"
                    title="Open QR Code"
                    aria-label="Open QR Code"
                  >
                    <img src={qrCodeIcon} alt="" className="w-9 h-9 opacity-90" aria-hidden="true" />
                    <span className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                      <span
                        className="flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white text-[#1b1b1b] shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#1b1b1b]"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </span>
                    </span>
                  </button>
                  <div
                    className={`flex items-center gap-2 ${isActive ? 'text-gray-500' : 'text-gray-600'}`}
                  >
                    <span className="material-symbols-outlined text-base" aria-hidden="true">bar_chart</span>
                    <span className="text-xs font-bold">
                      {new Intl.NumberFormat('en-US').format(getLinkClickDisplayCount(link))}{' '}
                      Clicks
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* QR Code Modal */}
      <Modal
        isOpen={qrModal.isOpen}
        onClose={() => {
          setQrModal({ isOpen: false, link: null });
          setQrCopyStatus('');
        }}
        title="QR Code"
        type="alert"
        hideIcon
        message={
          qrModal.link ? (
            <div className="flex flex-col items-center gap-2 sm:gap-4 p-0 sm:p-2 w-full min-w-0">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&bgcolor=FFFFFF&data=${encodeURIComponent(qrModal.link.short_url || '')}`}
                alt={`QR Code for ${qrModal.link.short_url || 'link'}`}
                className="w-full max-w-[200px] sm:max-w-[280px] md:max-w-[360px] h-auto rounded-xl border border-slate-200 bg-white p-1.5 sm:p-2"
              />
              <p className="text-[#1b1b1b] text-xs sm:text-sm font-mono break-all text-center max-w-full min-w-0 px-1">
                {qrModal.link.short_url}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 w-full">
                <button
                  type="button"
                  onClick={() => handleDownloadQrPng(qrModal.link.short_url)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-[#1b1b1b] text-sm font-medium transition-colors"
                >
                  <span className="material-symbols-outlined text-base" aria-hidden="true">download</span>
                  Download PNG
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadQrSvg(qrModal.link.short_url)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-[#1b1b1b] text-sm font-medium transition-colors"
                >
                  <span className="material-symbols-outlined text-base" aria-hidden="true">download</span>
                  Download SVG
                </button>
                <button
                  type="button"
                  onClick={() => handleCopyQrSvg(qrModal.link.short_url)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-[#1b1b1b] text-sm font-medium transition-colors"
                >
                  <span className="material-symbols-outlined text-base" aria-hidden="true">content_copy</span>
                  {qrCopyStatus || 'Copy SVG code'}
                </button>
              </div>
            </div>
          ) : null
        }
      />
      {/* Move Link Modal */}
      <Modal
        isOpen={moveModal.isOpen}
        onClose={closeMoveModal}
        title={`Move Link${moveModal.link ? `: ${moveModal.link.name || moveModal.link.short_url || ''}` : ''}`}
        type="confirm"
        confirmText={
          moveModal.selectedSpaceId === (moveModal.link?.space_id || null)
            ? 'Already Here'
            : 'Move To Here'
        }
        cancelText="Cancel"
        onConfirm={handleMoveLink}
        isLoading={moveModal.isSaving}
        message={
          <div className="space-y-4 text-left">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Current Selection
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setMoveModal((prev) => ({ ...prev, selectedSpaceId: null, error: null }))
                  }
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                    moveModal.selectedSpaceId === null
                      ? 'bg-[#0b996f]/15 border-[#0b996f]/60 text-[#0b996f]'
                      : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Root
                </button>
                {moveSelectionPath.map((space) => {
                  const isActive = moveModal.selectedSpaceId === space.id;
                  return (
                    <React.Fragment key={space.id}>
                      <ChevronRight size={12} className="text-[#1b1b1b]" aria-hidden="true" />
                      <button
                        type="button"
                        onClick={() =>
                          setMoveModal((prev) => ({
                            ...prev,
                            selectedSpaceId: space.id,
                            error: null,
                          }))
                        }
                        className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                          isActive
                            ? 'bg-[#0b996f]/15 border-[#0b996f]/60 text-[#0b996f]'
                            : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {space.name}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {`Available ${moveChildLabelPlural} Here`}
              </p>
              {moveChildSpaces.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {moveChildSpaces.map((space) => {
                    const isSelected = moveModal.selectedSpaceId === space.id;
                    return (
                      <button
                        key={space.id}
                        type="button"
                        onClick={() =>
                          setMoveModal((prev) => ({
                            ...prev,
                            selectedSpaceId: space.id,
                            error: null,
                          }))
                        }
                        className={`text-left px-3 py-2 rounded-xl border transition-colors ${
                          isSelected
                            ? 'border-[#0b996f] bg-[#0b996f]/10 text-[#0b996f]'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <div className="text-xs font-semibold truncate">{space.name}</div>
                        <div className="text-[10px] uppercase tracking-wider opacity-70 mt-1">
                          {KIND_LABEL[space.kind] || 'Space'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  {`No deeper ${moveChildLabelPlural.toLowerCase()} under this level. You can still move the link to the selected location.`}
                </p>
              )}
            </div>

            {moveModal.error ? <p className="text-sm text-red-500" role="alert">{moveModal.error}</p> : null}
          </div>
        }
      />
      {/* Error/Alert Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
        isLoading={modalState.isLoading}
      />
      {/* Link Details Modal */}
      <Modal
        isOpen={!!detailsLink}
        onClose={() => setDetailsLink(null)}
        title={detailsLink?.name || detailsLink?.short_url || 'Link Details'}
        message={
          detailsLink ? (
            <div className="space-y-3 text-left">
              {[
                { label: 'Name', value: detailsLink.name },
                { label: 'Short URL', value: detailsLink.short_url },
                { label: 'Domain', value: detailsLink.domain },
                { label: 'Slug', value: detailsLink.slug },
                { label: 'Target URL', value: detailsLink.target_url },
                { label: 'Fallback URL', value: detailsLink.fallback_url },
                { label: 'Status', value: detailsLink.status },
                { label: 'Tracking Mode', value: detailsLink.tracking_mode },
                { label: 'Server-Side Tracking', value: detailsLink.server_side_tracking != null ? (detailsLink.server_side_tracking ? 'Yes' : 'No') : null },
                { label: 'Created', value: detailsLink.created_at ? new Date(detailsLink.created_at).toLocaleString() : null },
                { label: 'Updated', value: detailsLink.updated_at ? new Date(detailsLink.updated_at).toLocaleString() : null },
              ]
                .filter(({ value }) => value != null && value !== '')
                .map(({ label, value }) => (
                  <div key={label} className="flex items-start justify-between py-2 border-b border-gray-200 gap-3">
                    <span className="text-xs font-medium text-[#1b1b1b] shrink-0 mr-4">{label}</span>
                    <div className="text-right max-w-[70%]">
                      <span className="text-sm text-[#1b1b1b] break-all font-mono">{value}</span>
                      {(label === 'Target URL' || label === 'Fallback URL') && (
                        <div className="mt-1 flex justify-end">
                          {urlStatusBadge(getDetailsUrlStatus(value))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              <div className="py-2 border-b border-gray-200">
                <span className="block text-xs font-medium text-[#1b1b1b] mb-2">Geo Redirect</span>
                {Array.isArray(detailsLink.geo_rules) && detailsLink.geo_rules.length > 0 ? (
                  <div className="space-y-2">
                    {detailsLink.geo_rules.map((rule, idx) => (
                      <div key={`${rule.country || 'country'}-${idx}`} className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-600">{rule.country || 'Country'}</p>
                          <p className="text-xs text-[#1b1b1b] break-all font-mono">{rule.url || '—'}</p>
                        </div>
                        <div className="shrink-0">{urlStatusBadge(getDetailsUrlStatus(rule.url))}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-slate-500">None</span>
                )}
              </div>
              <div className="flex items-start justify-between py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-[#1b1b1b] shrink-0 mr-4">Pixels</span>
                <span className="text-sm text-[#1b1b1b] break-all text-right max-w-[70%]">
                  {detailsPixelNames.length > 0
                    ? detailsPixelNames.map((p) => p.name).join(', ')
                    : 'None'}
                </span>
              </div>
            </div>
          ) : null
        }
        type="info"
      />
      {/* Delete Link Modal - rendered here so overlay covers full screen like Delete Workspace */}
      <Modal
        isOpen={deleteLinkModal.isOpen}
        onClose={() =>
          !deleteLinkModal.isLoading &&
          setDeleteLinkModal({ isOpen: false, link: null, isLoading: false })
        }
        title="Delete this link?"
        message={
          deleteLinkModal.link ? (
            <>
              Are you sure you want to delete <strong>{deleteLinkModal.link.short_url}</strong>?
              This will stop all traffic to this destination.
            </>
          ) : null
        }
        type="delete"
        confirmText="Delete Link"
        cancelText="Cancel"
        onConfirm={handleDeleteLink}
        isLoading={deleteLinkModal.isLoading}
      />
      {/* UTM Presets Modal */}
      <Modal
        isOpen={utmPresetsModal.isOpen}
        onClose={() => setUtmPresetsModal({ isOpen: false, link: null })}
        title="UTM Preset Links"
        message={
          <div className="space-y-3">
            {utmPresetsModal.link ? (
              <>
                <div className="text-sm text-slate-700">
                  <span className="font-semibold">Link:</span>{' '}
                  <span className="font-mono break-all">{utmPresetsModal.link.short_url}</span>
                </div>
                {renderUtmPresetsList(utmPresetsModal.link)}
              </>
            ) : (
              <div className="text-sm text-slate-700">No data.</div>
            )}
          </div>
        }
        type="info"
      />
      {/* Create Workspace / Campaign / Group modal */}
      <Modal
        isOpen={spaceModal.isOpen}
        onClose={closeCreateSpaceModal}
        title={`${spaceModal.mode === 'edit' ? 'Update' : 'Create'} ${spaceModal.kind ? KIND_LABEL[spaceModal.kind] : 'Item'}`}
        type="confirm"
        confirmText={spaceModal.mode === 'edit' ? 'Update' : 'Create'}
        cancelText="Cancel"
        onConfirm={handleSaveSpace}
        isLoading={spaceModal.isLoading}
        confirmButtonClass={spaceModal.mode === 'edit' ? 'btn-update-workspace' : 'btn-primary'}
        message={
          <div className="space-y-3 text-left">
            <p className="text-sm text-slate-700">
              {spaceModal.mode === 'edit'
                ? 'Update the name.'
                : `Name your new ${spaceModal.kind ? KIND_LABEL[spaceModal.kind].toLowerCase() : 'item'}.`}
            </p>
            <input
              ref={spaceNameInputRef}
              type="text"
              value={spaceModal.name}
              onChange={(e) =>
                setSpaceModal((prev) => ({ ...prev, name: e.target.value, error: null }))
              }
              autoFocus
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder={`Enter ${spaceModal.kind ? KIND_LABEL[spaceModal.kind] : 'item'} name`}
              aria-label={`${spaceModal.kind ? KIND_LABEL[spaceModal.kind] : 'Item'} name`}
            />
            <div className="min-h-[20px]">
              {spaceModal.error ? <p className="text-sm text-red-500" role="alert">{spaceModal.error}</p> : null}
            </div>
          </div>
        }
      />
    </div>
  );
};

// Actions Menu Component
const LinkActionsMenu = ({
  link,
  onEdit,
  onDuplicate,
  onAnalytics,
  onMove,
  onDeleteClick,
  onDetails,
  className = '',
  hoverBorderClass = 'hover:border-primary/40',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isRejected = (link?.status || '').toLowerCase() === 'rejected';

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleDuplicate = () => {
    setIsOpen(false);
    if (onDuplicate) {
      onDuplicate(link);
    }
  };

  return (
    <div className={`z-10 ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:text-[#1b1b1b] ${hoverBorderClass} transition-colors`}
        aria-label="Actions menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="material-symbols-outlined text-base" aria-hidden="true">more_vert</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div
            ref={menuRef}
            role="menu"
            aria-label="Link actions"
            className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-2xl z-20 overflow-hidden min-w-max"
          >
            <button
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                if (onDetails) onDetails(link);
              }}
              className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors flex items-center gap-3 text-sm"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">info</span>
              Details
            </button>
            {!isRejected && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(link);
                }}
                className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors flex items-center gap-3 text-sm"
              >
                <span className="material-symbols-outlined text-base" aria-hidden="true">edit</span>
                Edit
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                if (onAnalytics) onAnalytics(link);
              }}
              className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors flex items-center gap-3 text-sm"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">insights</span>
              Analytics
            </button>
            <button
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                handleDuplicate();
              }}
              className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors flex items-center gap-3 text-sm"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">content_copy</span>
              Duplicate
            </button>
            {onMove && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  onMove(link);
                }}
                className="w-full px-4 py-3 text-left text-[#1b1b1b] hover:bg-white/5 transition-colors flex items-center gap-3 text-sm"
              >
                <span className="material-symbols-outlined text-base" aria-hidden="true">drive_file_move</span>
                Move
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                if (onDeleteClick) onDeleteClick(link);
              }}
              className="w-full px-4 py-3 text-left text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-3 text-sm"
            >
              <span className="material-symbols-outlined text-base" aria-hidden="true">delete</span>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LinkManager;
